import {
  CANONICAL_MINTS,
  decimal,
  formatNative,
  nativeUnits,
  unsignedNative,
  type FundingSnapshot,
  type Pool,
} from "@auto-pools/core";

export function convertFunding(
  text: string,
  currency: "token" | "usd",
  price: string | null,
  decimals: number,
): { token: string; usd: string } {
  if (!/^\d+(\.\d+)?$/.test(text)) throw new Error("Enter a decimal amount");
  const rate = price === null ? null : decimal(price);
  const validRate = rate && rate.units > 0n ? rate : null;
  if (currency === "token") {
    const amount = nativeUnits(text, decimals);
    return {
      token: text,
      usd: validRate ? decimal(formatNative(amount, decimals)).mul(validRate).toString() : "",
    };
  }
  if (!validRate) throw new Error("USD price unavailable");
  const token = decimal(text).div(validRate, decimals).toString();
  return { token, usd: decimal(token).mul(validRate).toString() };
}
export interface FundingEvaluation {
  kind: "valid" | "invalid" | "refresh" | "inventory" | "insufficient";
  message: string | null;
}
export function fundingStale(snapshot: FundingSnapshot | null, now = Date.now()): boolean {
  const age = snapshot ? now - Date.parse(snapshot.observedAt) : NaN;
  return !Number.isFinite(age) || age < 0 || age > 60_000;
}
export function evaluateFunding(
  snapshot: FundingSnapshot | null,
  mint: string,
  amount: string,
  network: string,
  rent: string,
  reserve: string,
  now = Date.now(),
): FundingEvaluation {
  const result = (kind: FundingEvaluation["kind"], message: string | null) => ({ kind, message });
  try {
    if (!/^\d+(\.\d+)?$/.test(amount) || decimal(amount).units <= 0n) throw new Error();
    for (const cap of [network, rent, reserve]) nativeUnits(cap, 9);
  } catch {
    return result("invalid", "Enter valid amounts within token precision");
  }
  if (!snapshot) return result("refresh", "Refresh funding balances before continuing");
  const age = now - Date.parse(snapshot.observedAt);
  if (!Number.isFinite(age) || age < 0 || age > 60_000)
    return result("refresh", "Funding snapshot is stale. Continue to refresh balances.");
  const asset = snapshot.assets.find((a) => a.mint === mint);
  const sol = snapshot.assets.find((a) => a.mint === CANONICAL_MINTS.SOL);
  if (
    !asset ||
    asset.availableNative === null ||
    sol?.availableNative == null ||
    snapshot.reserveFloorLamports === null
  )
    return result("inventory", "Available balance is unknown; refresh before continuing");
  try {
    const spend = nativeUnits(amount, asset.decimals);
    if (spend <= 0n) return result("invalid", "Enter a positive funding amount");
    if (spend > unsignedNative(asset.availableNative))
      return result("insufficient", "Amount exceeds available balance");
    const minimum = nativeUnits(reserve, 9),
      pending = unsignedNative(snapshot.reserveFloorLamports);
    const floor = minimum > pending ? minimum : pending;
    const needed =
      nativeUnits(network, 9) +
      nativeUnits(rent, 9) +
      floor +
      (mint === CANONICAL_MINTS.SOL ? spend : 0n);
    return needed > unsignedNative(sol.availableNative)
      ? result(
          "insufficient",
          "Not enough available SOL. Funding, fees, account rent and your SOL reserve must all be covered.",
        )
      : result("valid", null);
  } catch {
    return result("invalid", "Enter valid amounts within token precision");
  }
}

export function fundingProblem(...args: Parameters<typeof evaluateFunding>): string | null {
  return evaluateFunding(...args).message;
}
/** The API client casts JSON; validate additive price/provenance fields before using them. */
export function validateFundingSnapshot(
  input: unknown,
  pool: Pick<Pool, "address" | "tokenA" | "tokenB">,
  wallet: { address?: string; generation?: string } | null,
): FundingSnapshot {
  const value = input as Partial<FundingSnapshot> | null;
  const assets = value?.assets;
  const p = value?.poolPrice as
    | { price?: unknown; baseMint?: unknown; quoteMint?: unknown; commitment?: unknown }
    | undefined;
  if (
    !value ||
    !p ||
    p.commitment !== "confirmed" ||
    p.baseMint !== pool.tokenA.mint ||
    p.quoteMint !== pool.tokenB.mint ||
    typeof p.price !== "string" ||
    !/^\d+(\.\d+)?$/.test(p.price) ||
    decimal(p.price).units <= 0n ||
    !(
      value.inventorySlot === null ||
      (typeof value.inventorySlot === "string" && /^(0|[1-9]\d*)$/.test(value.inventorySlot))
    ) ||
    value.poolAddress !== pool.address ||
    value.wallet?.address !== wallet?.address ||
    typeof value.wallet?.generation !== "string" ||
    (wallet?.generation && value.wallet.generation !== wallet.generation) ||
    !Array.isArray(assets) ||
    [pool.tokenA, pool.tokenB].some(
      (t) => !assets.some((a) => a.mint === t.mint && a.decimals === t.decimals),
    )
  )
    throw new Error("Wallet or pool observation invalid; refresh funding");
  for (const asset of assets) {
    for (const native of [asset.availableNative, asset.balanceNative, asset.allocatedNative])
      if (native !== null) unsignedNative(native);
    for (const usd of [asset.priceUsd, asset.availableUsd])
      if (usd !== null && (typeof usd !== "string" || !/^\d+(\.\d+)?$/.test(usd)))
        throw new Error("Funding valuation invalid");
  }
  if (value.reserveFloorLamports !== null) {
    if (typeof value.reserveFloorLamports !== "string") throw new Error("Funding reserve invalid");
    unsignedNative(value.reserveFloorLamports);
  }
  if (
    !Array.isArray(value.errors) ||
    !value.errors.every((e) => typeof e === "string") ||
    typeof value.priceSource !== "string"
  )
    throw new Error("Funding observation invalid");
  return value as FundingSnapshot;
}
